import { Link } from "react-router-dom";
import MultiModalInput from "../components/MultiModalInput";
import HeroDemoLoop from "../components/HeroDemoLoop";
import {
  TestTube, BracketsCurly, Scales, CalendarBlank, Calendar,
  MagnifyingGlass, ShoppingCart, Books, Database, ArrowUpRight,
  Lightning, ShieldCheck,
} from "@phosphor-icons/react";

/**
 * NUTRIENT/LAB · Billboard homepage.
 * Bold sans-serif blocks stacked vertically, each block = one product surface.
 * Palette: jet-black bg + electric-lime primary + white counter-blocks.
 * No background images — pure color, type, and shape.
 */

const BLOCKS = [
  {
    to: "/",
    title: "Nutrient Calculator",
    kicker: "01 · Multimodal",
    body: "Paste text, drop a link, upload audio, video, or a photo. Gemini 3.1 Pro decodes the recipe and returns 23 nutrients per serving.",
    cta: "Start calculating",
    tone: "primary",
    icon: TestTube,
    inline: true,
  },
  {
    to: "/builder",
    title: "Recipe Builder",
    kicker: "02 · Row by row",
    body: "Add ingredients with 13 units and grams normalization. Live macro + micro preview updates on every keystroke.",
    cta: "Build a recipe",
    tone: "lime",
    icon: BracketsCurly,
  },
  {
    to: "/compare",
    title: "Compare",
    kicker: "03 · Side-by-side",
    body: "Line up any two recipes and see calories, protein density, PDCAAS score, micronutrients — nothing hidden.",
    cta: "Compare recipes",
    tone: "cobalt",
    icon: Scales,
  },
  {
    to: "/planner",
    title: "Meal Planner",
    kicker: "04 · Family-scale",
    body: "Plan seven days across four slots. Add multiple people, run auto-plan against DRIs, flag allergies and choking hazards for kids.",
    cta: "Open planner",
    tone: "sunny",
    icon: CalendarBlank,
  },
  {
    to: "/log",
    title: "Daily Log",
    kicker: "05 · Progress",
    body: "Log meals in one tap. Track weekly averages against your DRI and see exactly which nutrients are trending short.",
    cta: "Log a day",
    tone: "crimson",
    icon: Calendar,
  },
  {
    to: "/search",
    title: "Web Search",
    kicker: "06 · Discovery",
    body: "Search the open web for recipes matching your macros. We fetch, parse, and rank them by nutrition — not vibes.",
    cta: "Search recipes",
    tone: "violet",
    icon: MagnifyingGlass,
  },
  {
    to: "/grocery",
    title: "Grocery",
    kicker: "07 · Aisle by aisle",
    body: "Turn any recipe into a checklist, sorted by store layout. Never make a second loop around the produce section again.",
    cta: "Open grocery",
    tone: "teal",
    icon: ShoppingCart,
  },
  {
    to: "/library",
    title: "Library",
    kicker: "08 · Archive",
    body: "Every recipe you compute is saved. Diet tags, PDCAAS score, and 14-allergen warning chips — searchable and sortable.",
    cta: "Open library",
    tone: "graphite",
    icon: Books,
  },
  {
    to: "/database",
    title: "Food Database",
    kicker: "09 · Reference",
    body: "USDA, FAO INFOODS, CIQUAL. Curated per-100g values for the fundamentals; AI-backed research estimates for the exotic.",
    cta: "Browse foods",
    tone: "lime-outline",
    icon: Database,
  },
];

const TONE_STYLES = {
  primary:      { bg: "bg-[#0A0A0A]", text: "text-white", accent: "text-[#C8FF3F]", border: "border-white/10", chip: "border-[#C8FF3F]/50 text-[#C8FF3F]" },
  lime:         { bg: "bg-[#C8FF3F]", text: "text-black", accent: "text-black", border: "border-black/20", chip: "border-black/40 text-black" },
  cobalt:       { bg: "bg-[#1E40AF]", text: "text-white", accent: "text-[#FFD84D]", border: "border-white/15", chip: "border-white/40 text-white" },
  sunny:        { bg: "bg-[#FFD84D]", text: "text-black", accent: "text-black", border: "border-black/20", chip: "border-black/40 text-black" },
  crimson:      { bg: "bg-[#DC2626]", text: "text-white", accent: "text-white", border: "border-white/20", chip: "border-white/40 text-white" },
  violet:       { bg: "bg-[#6D28D9]", text: "text-white", accent: "text-[#C8FF3F]", border: "border-white/15", chip: "border-[#C8FF3F]/50 text-[#C8FF3F]" },
  teal:         { bg: "bg-[#0F766E]", text: "text-white", accent: "text-white", border: "border-white/15", chip: "border-white/40 text-white" },
  graphite:     { bg: "bg-[#111827]", text: "text-white", accent: "text-[#C8FF3F]", border: "border-white/10", chip: "border-white/40 text-white" },
  "lime-outline": { bg: "bg-white", text: "text-black", accent: "text-black", border: "border-black", chip: "border-black text-black" },
};

function BillboardBlock({ block, index }) {
  const t = TONE_STYLES[block.tone];
  const Icon = block.icon;
  return (
    <section
      className={`${t.bg} ${t.text} border-y ${t.border} px-6 lg:px-10 py-16 md:py-24 lg:py-28 relative overflow-hidden`}
      data-testid={`billboard-${block.title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="mx-auto max-w-[1400px]">
        {/* Kicker + icon */}
        <div className="flex items-center justify-between mb-6 md:mb-10">
          <span className={`font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] ${t.accent}`}>{block.kicker}</span>
          <Icon size={44} weight="fill" className={`${t.accent} opacity-90`} />
        </div>

        {/* Title */}
        <h2
          className={`font-display font-black leading-[0.9] tracking-tighter ${t.text}`}
          style={{ fontSize: "clamp(3rem, 9vw, 9.5rem)" }}
        >
          {block.title}
          <span className={`${t.accent}`}>.</span>
        </h2>

        {/* Body */}
        <p className={`mt-6 md:mt-8 max-w-3xl text-lg md:text-2xl leading-snug font-medium ${t.text} opacity-90`}>
          {block.body}
        </p>

        {/* Inline calculator input (only on the first block) */}
        {block.inline && (
          <div className="mt-10 md:mt-14">
            <MultiModalInput />
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 md:mt-14 flex flex-wrap items-center gap-3">
          <Link
            to={block.to}
            data-testid={`billboard-cta-${index}`}
            className={`group inline-flex items-center gap-3 px-6 md:px-8 h-14 md:h-16 border-2 ${t.border.replace("/10", "").replace("/15", "").replace("/20", "")} font-mono text-sm md:text-base uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors duration-200 rounded-full`}
          >
            {block.cta}
            <ArrowUpRight size={18} weight="bold" className="group-hover:rotate-45 transition-transform duration-300" />
          </Link>
          <span className={`inline-flex items-center px-3 py-1 border rounded-full font-mono text-[10px] uppercase tracking-[0.25em] ${t.chip}`}>
            {String(index + 1).padStart(2, "0")} / {String(BLOCKS.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="relative bg-white text-black" data-testid="home-page">

      {/* HERO — the loudest billboard */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10 px-6 lg:px-10 pt-16 md:pt-24 pb-10 md:pb-14 relative overflow-hidden">
        {/* Silent looping recipe-parse demo — clickable to freeze + prefill calculator */}
        <HeroDemoLoop
          onIngredientClick={({ recipe }) => {
            // Broadcast to the MultiModalInput below so it switches to Text mode + prefills.
            window.dispatchEvent(new CustomEvent("demo:freeze-prefill", { detail: { recipe } }));
            // Smooth-scroll to the calculator input inside the first billboard.
            requestAnimationFrame(() => {
              const el = document.querySelector('[data-testid="multimodal-input"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
          }}
        />

        <div className="mx-auto max-w-[1400px] relative z-10">
          <div className="inline-flex items-center gap-2 border border-[#C8FF3F]/50 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C8FF3F]">
            <Lightning size={12} weight="fill" /> Research-grade · USDA · FAO · CIQUAL
          </div>
          <h1
            className="mt-6 font-display font-black leading-[0.88] tracking-tighter"
            style={{ fontSize: "clamp(3.5rem, 11vw, 12rem)" }}
          >
            EAT LIKE<br />
            YOU <span className="text-[#C8FF3F]">READ</span><br />
            THE LABEL.
          </h1>
          <p className="mt-8 max-w-2xl text-xl md:text-2xl leading-snug font-medium opacity-90">
            A lab-grade <strong className="text-[#C8FF3F] font-black">nutrient calculator</strong>, <strong className="text-white font-black">recipe builder</strong>, <strong className="text-white font-black">recipe search engine</strong>, <strong className="text-white font-black">meal planner</strong>, and <strong className="text-white font-black">grocery planner</strong> — powered by Gemini 3.1 Pro and 44 nutrients per serving. Zero fluff. Full science.
          </p>

          {/* Hero CTA buttons — the four core surfaces */}
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-cta-row">
            {[
              { to: "/",        label: "Nutrient Calculator", tone: "primary" },
              { to: "/search",  label: "Recipe Search",       tone: "ghost" },
              { to: "/planner", label: "Meal Planner",        tone: "ghost" },
              { to: "/grocery", label: "Grocery Planner",     tone: "ghost" },
            ].map((c, i) => (
              <Link
                key={c.to + i}
                to={c.to}
                data-testid={`hero-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={
                  c.tone === "primary"
                    ? "group inline-flex items-center gap-2.5 px-5 md:px-7 h-13 md:h-14 bg-[#C8FF3F] text-black font-mono text-xs md:text-sm uppercase tracking-[0.18em] hover:bg-white transition-colors duration-200 rounded-full font-bold"
                    : "group inline-flex items-center gap-2.5 px-5 md:px-7 h-13 md:h-14 border-2 border-white/30 text-white hover:border-[#C8FF3F] hover:text-[#C8FF3F] font-mono text-xs md:text-sm uppercase tracking-[0.18em] transition-colors duration-200 rounded-full"
                }
              >
                {c.label}
                <ArrowUpRight size={16} weight="bold" className="group-hover:rotate-45 transition-transform duration-300" />
              </Link>
            ))}
          </div>

          {/* Stats strip */}
          <div className="mt-10 md:mt-14 grid grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden max-w-3xl">
            {[
              { k: "44", l: "Nutrients / serving" },
              { k: "9", l: "Tools included" },
              { k: "14", l: "Allergen tags" },
            ].map(s => (
              <div key={s.l} className="bg-[#0A0A0A] p-6 md:p-8">
                <div className="font-display font-black text-4xl md:text-6xl tracking-tighter text-[#C8FF3F]">{s.k}</div>
                <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/60 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature billboards — one per product surface */}
      {BLOCKS.map((b, i) => <BillboardBlock key={b.title} block={b} index={i} />)}

      {/* Trust / stack strip */}
      <section className="bg-black text-white px-6 lg:px-10 py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] grid gap-10 lg:grid-cols-[1fr_1fr] items-center">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.35em] text-[#C8FF3F] mb-4">Powered by</div>
            <h3 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.9]">
              Real science.<br />No influencers.
            </h3>
          </div>
          <ul className="space-y-4 text-lg md:text-xl leading-snug font-medium">
            <li className="flex items-start gap-3"><ShieldCheck size={22} className="text-[#C8FF3F] shrink-0 mt-1" weight="fill" /> USDA FoodData Central per-100g values for every curated ingredient.</li>
            <li className="flex items-start gap-3"><ShieldCheck size={22} className="text-[#C8FF3F] shrink-0 mt-1" weight="fill" /> FAO/WHO amino-acid reference pattern for PDCAAS protein quality.</li>
            <li className="flex items-start gap-3"><ShieldCheck size={22} className="text-[#C8FF3F] shrink-0 mt-1" weight="fill" /> Age-banded DRIs from IOM/EFSA — including child, teen, and pregnancy bands.</li>
            <li className="flex items-start gap-3"><ShieldCheck size={22} className="text-[#C8FF3F] shrink-0 mt-1" weight="fill" /> 14 major allergens per EU FIC, FDA FALCPA + FASTER Act, and Codex Alimentarius.</li>
          </ul>
        </div>
      </section>

      {/* Legal disclaimer */}
      <footer className="bg-white text-black border-t-2 border-black px-6 lg:px-10 py-14" data-testid="legal-footer">
        <div className="mx-auto max-w-[1400px] space-y-8">
          <div className="grid gap-6 md:grid-cols-[auto_1fr] items-start">
            <div className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.9]">
              THE FINE<br />PRINT.
            </div>
            <div className="space-y-4 text-sm md:text-[15px] leading-relaxed text-black/80 max-w-3xl">
              <p><strong className="text-black">Not medical advice.</strong> NUTRIENT/LAB is an informational research tool, not a substitute for professional medical, dietetic, or nutritional advice, diagnosis, or treatment. Always consult a qualified healthcare provider — especially if you have allergies, food intolerances, chronic conditions, are pregnant or breastfeeding, or are planning meals for infants and children.</p>
              <p><strong className="text-black">Estimates only.</strong> All nutrient values are best-effort estimates derived from public research databases (USDA FoodData Central, FAO INFOODS, CIQUAL) and AI reasoning where curated data is unavailable. Actual values vary by variety, ripeness, cooking method, brand, and portion. For allergy-critical decisions, always verify against the product's printed label and consult the manufacturer.</p>
              <p><strong className="text-black">Third-party content.</strong> When you paste a link, we fetch publicly available text (and, for YouTube, public captions) to extract ingredients. All original recipes, video content, and captions remain the property of their respective authors and platforms. NUTRIENT/LAB claims no ownership over scraped or transcribed content and does not redistribute it — we compute nutrition from it. If you are a rights holder and would like content excluded, please contact us.</p>
              <p><strong className="text-black">Kid-safe flags.</strong> Choking-hazard warnings follow AAP general guidance and are indicative only — never a substitute for parental supervision at mealtime.</p>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/50 pt-4 border-t border-black/10">© {new Date().getFullYear()} NUTRIENT/LAB · Data: USDA · FAO INFOODS · CIQUAL · AI: Gemini 3.1 Pro</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
